import { useEffect, useState, type CSSProperties, type ReactNode } from 'react'
import { useGoogleLogin } from '@react-oauth/google'
import { getStoredSession, setStoredSession, clearStoredSession, isDeviceToken, type TenantSession } from './lib/session'
import { verifyTenantLink, requestTenantLink, getMe, listValidations, listTenantPermits, getResidentConfig, listTenantScopes, switchTenantScope, tenantCan, signInTenantStaffWithGoogle, listStaffTenants, openStaffTenant, suppressReadOnlyAlerts, type MeResponse, type TenantScope, type StaffTenant } from './lib/api'
import { Validate } from './screens/Validate'
import { Validation } from './screens/Validation'
import { Settings } from './screens/Settings'
import { Parkers } from './screens/Parkers'
import { Permits } from './screens/Permits'
import { Billing } from './screens/Billing'
import { Register } from './screens/Register'
import { ResidentGuest } from './screens/ResidentGuest'
import { ResidentImports } from './screens/ResidentImports'
import { UsersPanel } from './screens/UsersPanel'
import { AddParkerActions } from './components/AddParkerActions'
import { ConfirmSignInLink } from './components/ConfirmSignInLink'
import { TenantWelcomeModal, type WelcomeDestination, type WelcomeSection } from './components/TenantWelcomeModal'
import { dismissTenantWelcome, shouldShowTenantWelcome, summarizeTenantWelcomeRoster, welcomeStorageFor, type TenantWelcomeRosterSummary } from './lib/welcome'
import { ViewAsBanner, ReadOnlyToast } from './components/ViewAsBanner'
import { ghostBtn, purpleBtn, selectStyle } from './ui'
import { TenantNavigation } from './components/TenantNavigation'

// A tablet link carries the current code for its bound validation program
// (`?code=`). Read once, at module load, BEFORE the `?d=` effect scrubs the
// query string — otherwise the initial screen hint would be gone by the time
// Validate mounts. The server remains authoritative for the durable binding.
const INITIAL_CODE_PARAM = new URLSearchParams(window.location.search).get('code')?.trim().toUpperCase() || ''

// Staff self-registration link: `?r=<tenantId>`. Read at module load like the
// params above, and deliberately a QUERY param on the root rather than a
// `/register` path — this app is served as a static SPA, so a real path would
// depend on a catch-all rewrite that the other two entry points (`?d=`,
// `?token=`) don't need.
const REGISTER_TENANT_PARAM = new URLSearchParams(window.location.search).get('r')?.trim() || ''
// `&type=<permitType>` — which permit the link signs people up FOR. Advisory:
// the server re-validates it against the closed union and falls back to the
// default bucket, so a forwarded link that lost the param still works.
const REGISTER_TYPE_PARAM = new URLSearchParams(window.location.search).get('type')?.trim() || ''
// `&reserved=1` — a program sign-up for a RESERVED numbered bay. Advisory like
// the others: the server confirms the tenant's pool can honour it.
const REGISTER_RESERVED_PARAM = new URLSearchParams(window.location.search).get('reserved') === '1'
// `&program=<programId>` — a shared program the operator allocated seats to
// this tenant. Unlike `type`, NOT advisory: the server refuses an unknown or
// foreign program rather than queueing the request untagged.
const REGISTER_PROGRAM_PARAM = new URLSearchParams(window.location.search).get('program')?.trim() || ''
// `&permit=<permitId>` — one of a named-permit tenant's own permits. Not
// advisory, like `program`: the server refuses one that is not the tenant's.
const REGISTER_PERMIT_PARAM = new URLSearchParams(window.location.search).get('permit')?.trim() || ''
const RESIDENT_GUEST_TENANT_PARAM = new URLSearchParams(window.location.search).get('g')?.trim() || ''

// The validation host is a single-purpose entry point for counter tablets.
// A station is paired by opening the one-time `?d=ptd_…` link generated in the
// tenant portal; without that device credential we must stop at the pairing
// gate instead of falling through to the tenant staff sign-in screen.
const IS_VALIDATION_SURFACE = window.location.hostname === 'validation.parkify.com'

export default function App() {
  const [session, setSession] = useState<TenantSession | null>(() => {
    const stored = getStoredSession()
    // Never reuse a manager's cached session on the public kiosk host. A
    // validation tablet may only boot from its device credential; otherwise a
    // shared counter could accidentally expose the full tenant portal.
    return IS_VALIDATION_SURFACE && stored?.kind !== 'device' ? null : stored
  })
  const [me, setMe] = useState<MeResponse | null>(null)
  const [tenantScopes, setTenantScopes] = useState<TenantScope[]>([])
  const [verifying, setVerifying] = useState(false)
  const [authError, setAuthError] = useState('')
  // `validations.view` describes the user's permission, not whether this tenant
  // has a validation program. Austin's current demo keeps those two absences
  // separate: a tenant with no program should not get an empty Validation tab.
  // `null` is deliberately the compatible loading/unknown state so a failed
  // discovery read never hides a capability the server granted.
  const [hasValidationPrograms, setHasValidationPrograms] = useState<boolean | null>(null)
  // Resident guest parking is a staff-controlled rollout. Keep the discovery
  // read separate from permissions so a tenant with the feature disabled (the
  // default for existing tenants) never sees a speculative nav item.
  const [residentGuestEnabled, setResidentGuestEnabled] = useState<boolean | null>(null)
  const [permitSubtitle, setPermitSubtitle] = useState<string | null>(null)
  const [billingSubtitle, setBillingSubtitle] = useState<string | null>(null)
  // Parkers is the landing tab because it is the first nav item and the screen
  // a manager actually opens the portal for. Validation led back when it was
  // the only tab; a front-desk *tablet* still lands on Validate, but it never
  // reaches this state at all (see isTablet below).
  const [tab, setTab] = useState<'validation' | 'parkers' | 'permits' | 'billing' | 'resident' | 'settings'>('parkers')
  /**
   * A permit type handed to Parkers when a manager drills in from a Permits
   * capacity card ("View parkers →"). Lives here because it crosses tabs.
   * One-shot: cleared by any nav click, so it can't resurface later.
   * `permitId` is set when the card was one named permit, not a whole type.
   */
  const [parkersDrill, setParkersDrill] = useState<{ type: string; permitId?: string } | null>(null)
  const [openAddParker, setOpenAddParker] = useState(false)
  const [openIssueValidation, setOpenIssueValidation] = useState(false)
  const [usersOpen, setUsersOpen] = useState(false)
  const [welcomeOpen, setWelcomeOpen] = useState(false)
  const [welcomeRoster, setWelcomeRoster] = useState<TenantWelcomeRosterSummary | null>(null)
  const [welcomeValidationCount, setWelcomeValidationCount] = useState<number | null>(null)
  const [welcomeDetailsReady, setWelcomeDetailsReady] = useState(false)

  // Consume a front-desk tablet link `?d=<ptd_…>` once on load (tenant_devices.ts).
  // Unlike the magic link below there's nothing to exchange: the credential IS
  // the bearer token, presented on every request so that revoking the device
  // takes effect immediately rather than whenever a session token would have
  // expired. We keep it in localStorage so the tablet stays signed in across
  // shifts, and scrub it out of the URL right away — a counter tablet shouldn't
  // sit there with its credential in the address bar for anyone to read or
  // screenshot. (The tenant is learned from /api/tenants/me, so nothing else
  // needs to ride in the link.)
  useEffect(() => {
    const d = new URLSearchParams(window.location.search).get('d')?.trim()
    if (!d) return
    const scrub = () => window.history.replaceState({}, '', window.location.pathname)
    if (!isDeviceToken(d)) { scrub(); setAuthError('That tablet link is not valid. Ask your operator for a new one.'); return }
    // A device link supersedes whatever was stored — re-scanning a fresh link is
    // exactly how an operator replaces a revoked credential on the same tablet.
    // The `?code=` rides along into storage so a reload doesn't lose the offer
    // this tablet was set up for (the query string is gone after the scrub).
    setSession(setStoredSession(d, 'Front desk tablet', '', 'device', INITIAL_CODE_PARAM || undefined))
    setAuthError('')
    scrub()
  }, [])

  // A magic-link `?token=` is held, NOT redeemed, on load. The redeem runs from
  // the confirm button (ConfirmSignInLink explains why: mail-gateway scanners
  // load the page and would otherwise spend the single-use token first).
  //
  // A held link always wins over a stored session. It used to be skipped when
  // one existed (`|| session`) — the idea being a bookmarked/reopened link
  // shouldn't bother re-verifying if you're already signed in. But that guard
  // fires on ANY cached session, including a stale or wrong one (e.g. an ops
  // admin's browser still holding a `tenant-admin-lens` session from a PREVIOUS
  // "sign in as tenant" click, possibly for a different tenant). A fresh link
  // was then silently discarded and the app fell through to the stale
  // session, which 401'd on the first API call and dumped the admin onto the
  // sign-in screen — reading as "sign in as tenant doesn't work" with no
  // indication the token was never even checked. A link in the URL is an
  // explicit, deliberate sign-in attempt; it must always be offered and always
  // win over whatever's cached.
  const [pendingLinkToken, setPendingLinkToken] = useState(() => new URLSearchParams(window.location.search).get('token') || '')
  const redeemLinkToken = async () => {
    if (!pendingLinkToken || verifying) return
    setVerifying(true)
    try {
      const res = await verifyTenantLink(pendingLinkToken)
      setSession(setStoredSession(res.token, res.email, res.tenantId, 'user', undefined, res.viewAs))
    } catch {
      setAuthError('This sign-in link is invalid or has expired. Request a new one below.')
    } finally {
      // Spent either way: success made a session, failure means the server
      // refused it. Scrub it so a reload doesn't offer a dead button.
      window.history.replaceState({}, '', window.location.pathname)
      setPendingLinkToken('')
      setVerifying(false)
    }
  }

  // A 401 on any authed call (api.ts) means the session expired/was revoked
  // mid-use — drop straight to sign-in with a clear message instead of leaving
  // the current screen stuck on a generic "Couldn't load".
  useEffect(() => {
    const onExpired = () => {
      const wasLens = !!getStoredSession()?.viewAs
      clearStoredSession()
      if (wasLens) {
        // The lens ended (target revoked, staff access changed, or the hour
        // ran out). Fall back to whatever real session this tab had.
        setSession(getStoredSession())
        setMe(null)
        setAuthError('View as ended. Open it again from the console.')
        return
      }
      setSession(null)
      setMe(null)
      setTab('parkers')
      setUsersOpen(false)
      setAuthError('Your session expired — sign in again.')
    }
    window.addEventListener('parkify:session-expired', onExpired)
    return () => window.removeEventListener('parkify:session-expired', onExpired)
  }, [])

  // Load the signed-in tenant.
  useEffect(() => {
    if (!session) {
      setMe(null); setHasValidationPrograms(null); setResidentGuestEnabled(null); setPermitSubtitle(null); setBillingSubtitle(null)
      setWelcomeRoster(null); setWelcomeValidationCount(null); setWelcomeDetailsReady(false); return
    }
    setWelcomeRoster(null); setWelcomeValidationCount(null); setWelcomeDetailsReady(false)
    void (async () => {
      try {
        const nextMe = await getMe(session.token)
        setMe(nextMe)
        // A missing scopes endpoint during a rolling API deploy is not an
        // expired session. Keep the normal single-company portal usable.
        // A view-as lens is pinned to one tenant: no switcher.
        if (session.viewAs) setTenantScopes([])
        else {
          try { setTenantScopes((await listTenantScopes(session.token)).tenants) }
          catch { setTenantScopes([]) }
        }
        if (!tenantCan(nextMe, 'users.manage')) {
          setResidentGuestEnabled(false)
        } else {
          try {
            const residentConfig = await getResidentConfig(session.token)
            setResidentGuestEnabled(residentConfig.enabled)
          } catch {
            // A rollout discovery failure must not block the normal tenant
            // portal, and should fail closed for this optional surface.
            setResidentGuestEnabled(null)
          }
        }
        if (!tenantCan(nextMe, 'validations.view')) {
          setHasValidationPrograms(false)
          setWelcomeValidationCount(null)
        } else {
          try {
            const programs = await listValidations(session.token)
            setHasValidationPrograms(programs.length > 0)
            setWelcomeValidationCount(programs.filter(program => program.active).length)
          } catch {
            // Capability remains the safe fallback when the discovery read is
            // unavailable during a rolling deploy or a transient outage.
            setHasValidationPrograms(null)
            setWelcomeValidationCount(null)
          }
        }
        // The modal can name what already exists instead of talking like an
        // empty account. Skip the extra roster read once this browser has
        // dismissed the welcome; the Parkers/Permits screens own their normal
        // reads and should not pay an onboarding tax forever.
        if (shouldShowTenantWelcome(session, welcomeStorageFor(session)) && (tenantCan(nextMe, 'parkers.view') || tenantCan(nextMe, 'permits.view'))) {
          try {
            setWelcomeRoster(summarizeTenantWelcomeRoster((await listTenantPermits(session.token)).permits))
          } catch {
            setWelcomeRoster(null)
          }
        }
        setWelcomeDetailsReady(true)
      }
      catch {
        // Only drop the session this load was for. A lens 401 has already been
        // handled by onExpired, which may have restored a real session here.
        if (getStoredSession()?.token !== session.token) return
        clearStoredSession(); setSession(session.viewAs ? getStoredSession() : null)
        setAuthError(session.viewAs ? 'View as ended. Open it again from the console.' : 'Your session expired — sign in again.')
      }
    })()
  }, [session])

  // The introduction waits for /me so it can name the business and advertise
  // only capabilities this person actually has. A front-desk device never
  // reaches it: that credential is a single-purpose validation surface.
  useEffect(() => {
    if (!session || !me || !welcomeDetailsReady) { setWelcomeOpen(false); return }
    setWelcomeOpen(shouldShowTenantWelcome(session, welcomeStorageFor(session)))
  }, [session, me, welcomeDetailsReady])

  // A write attempted under the view-as lens (api.ts refuses it before fetch).
  const [readOnlyToast, setReadOnlyToast] = useState(false)
  const [viewAsBannerHeight, setViewAsBannerHeight] = useState(0)
  useEffect(() => {
    let timer: ReturnType<typeof setTimeout> | undefined
    const onReadOnly = () => {
      setReadOnlyToast(true)
      clearTimeout(timer)
      timer = setTimeout(() => setReadOnlyToast(false), 4000)
    }
    window.addEventListener('parkify:read-only', onReadOnly)
    return () => { window.removeEventListener('parkify:read-only', onReadOnly); clearTimeout(timer) }
  }, [])

  useEffect(() => {
    if (!session?.viewAs?.readOnly) return
    return suppressReadOnlyAlerts()
  }, [session])

  // Leave the lens. The console opened it in its own tab, so close that; if the
  // browser refuses (tab not script-opened), drop back to this tab's real
  // session, or to sign-in when there is none.
  const exitViewAs = () => {
    clearStoredSession()
    setMe(null); setTab('parkers'); setUsersOpen(false); setWelcomeOpen(false)
    window.close()
    setSession(getStoredSession())
  }

  const signOut = () => {
    clearStoredSession(); setSession(null); setMe(null); setHasValidationPrograms(null); setResidentGuestEnabled(null)
    setPermitSubtitle(null); setBillingSubtitle(null); setTab('parkers'); setUsersOpen(false); setWelcomeOpen(false)
  }

  const switchTenant = async (tenantId: string) => {
    if (!session || tenantId === session.tenantId) return
    const next = await switchTenantScope(session.token, tenantId)
    setSession(setStoredSession(next.token, session.email, next.tenantId))
    setTab('parkers'); setUsersOpen(false); setWelcomeOpen(false)
  }

  // Staff self-registration is deliberately NOT gated — it is the public link
  // staff receive, and a sign-in in front of it would defeat the point. It is
  // checked BEFORE the session gate for that reason, and before a held link so
  // that a manager who happens to be signed in on this device still sees the
  // form when they open the link to check it.
  if (REGISTER_TENANT_PARAM) return <Register tenantId={REGISTER_TENANT_PARAM} permitType={REGISTER_TYPE_PARAM} programId={REGISTER_PROGRAM_PARAM || undefined} permitId={REGISTER_PERMIT_PARAM || undefined} reserved={REGISTER_RESERVED_PARAM} />
  if (RESIDENT_GUEST_TENANT_PARAM) return <ResidentGuest tenantId={RESIDENT_GUEST_TENANT_PARAM} />

  if (IS_VALIDATION_SURFACE && !session) return <ValidationPairingGate error={authError} onPaired={(token, code) => setSession(setStoredSession(token, 'Front desk tablet', '', 'device', code))} />
  if (pendingLinkToken) return <ConfirmSignInLink busy={verifying} onContinue={() => void redeemLinkToken()} />
  if (!session) return <SignIn error={authError} onSignedIn={(next) => setSession(setStoredSession(next.token, next.email, next.tenantId, 'user', undefined, next.viewAs))} />
  if (!me) return <Centered>Loading your portal…</Centered>

  // A tablet is a single-purpose device: its credential can ONLY reach the
  // validate surface (see isDeviceRouteAllowed in the Worker), so rendering the
  // other tabs would just offer a clerk three buttons that 403. It also gets no
  // "Sign out" — signing out on a shared tablet strands it until someone brings
  // a fresh link, which is the exact failure this whole feature exists to remove.
  const isTablet = session.kind === 'device'
  // A tenant can span several lots. Remove account-wide monetary surfaces only
  // when EVERY assigned lot is validation-only; mixed tenants retain Billing
  // for their ordinary paid locations.
  const validationOnlyTenant = me.zones.length > 0 && me.zones.every(zone => zone.validationOnly === true)

  // A tablet is single-purpose (see isTablet above) and never shows the nav,
  // so it keeps the old plain top header rather than paying for a sidebar it
  // has no other tabs to put in.
  if (isTablet) {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
        <header style={{ background: '#fff', borderBottom: '1px solid var(--color-gray-200)', padding: '14px 24px', display: 'flex', alignItems: 'center', gap: 14 }}>
          <img src="/parkify-logo-color.svg" alt="Parkify" style={{ height: 20, width: 'auto', display: 'block' }} />
          <div style={{ fontWeight: 700, fontSize: 15 }}>{me.tenant.name}</div>
          <span style={{ fontSize: 12, color: 'var(--color-gray-600)' }}>Validate a car</span>
        </header>
        <main style={{ flex: 1, padding: '20px 24px 48px', maxWidth: 1100, width: '100%', margin: '0 auto' }}>
          {/* The stored code wins over the URL: on a tablet the URL is scrubbed
              within a tick of arrival, so storage is the durable copy. */}
          <Validate session={session} tenant={me.tenant} initialCode={session.defaultCode || INITIAL_CODE_PARAM} hideMonetaryValues={validationOnlyTenant} />
        </main>
      </div>
    )
  }

  // A validation-only user needs the only surface they are allowed to use even
  // when discovery is unavailable; other roles follow Austin's no-program
  // behavior once the tenant read is known. Shared by the Validation nav tab
  // AND the "Issue validation" quick action below — those two drifted once
  // already (Slack report, Lush Nail Bar/GA1110-A: the button showed with no
  // Validation tab in the nav), so both read this single predicate now.
  const tenantHasValidationProgram = hasValidationPrograms !== false || me.access?.level === 'validation'
  const visibleTabs = [
    tenantCan(me, 'parkers.view') && 'parkers',
    tenantCan(me, 'permits.view') && 'permits',
    tenantCan(me, 'validations.view') && tenantHasValidationProgram && 'validation',
    !validationOnlyTenant && tenantCan(me, 'billing.view') && 'billing',
    tenantCan(me, 'users.manage') && residentGuestEnabled === true && 'resident',
  ].filter((t): t is 'parkers' | 'permits' | 'validation' | 'billing' | 'resident' => Boolean(t))
  const canOpenSettings = tenantCan(me, 'users.manage')
  const effectiveTab = (tab === 'settings' ? canOpenSettings : visibleTabs.includes(tab))
    ? tab
    : (visibleTabs[0] ?? 'validation')
  const sectionMeta: Record<'parkers' | 'permits' | 'validation' | 'billing' | 'resident' | 'settings', [string, string]> = {
    parkers: ['Parkers', `Everyone registered to park at ${me.tenant.name}.`],
    permits: ['Permits', permitSubtitle ?? 'Parking permits for your team.'],
    validation: ['Validation', 'Issue validations and track usage.'],
    billing: ['Billing', billingSubtitle ?? 'Parking invoices and usage for the account.'],
    resident: ['Resident guest parking', 'Manage resident records and guest allowance imports.'],
    settings: ['Settings', 'Manage your portal and connected devices.'],
  }
  const [sectionTitle, sectionSubtitle] = sectionMeta[effectiveTab]
  const welcomeCopy: Record<WelcomeDestination, Omit<WelcomeSection, 'tab'>> = {
    parkers: {
      label: 'Parkers',
      description: welcomeRoster && welcomeRoster.parkerCount > 0
        ? `View and add staff. ${welcomeRoster.parkerCount} ${welcomeRoster.parkerCount === 1 ? 'parker' : 'parkers'} and ${welcomeRoster.plateCount} ${welcomeRoster.plateCount === 1 ? 'plate is' : 'plates are'} already loaded.`
        : 'View and add staff, review requests, and keep license plates current.',
    },
    permits: {
      label: 'Permits',
      description: welcomeRoster && welcomeRoster.permitCount > 0
        ? `${welcomeRoster.programCount} active permit ${welcomeRoster.programCount === 1 ? 'program' : 'programs'} and ${welcomeRoster.permitCount} ${welcomeRoster.permitCount === 1 ? 'permit is' : 'permits are'} already loaded.`
        : 'See your parking programs, capacity, and active coverage.',
    },
    validation: {
      label: 'Validation',
      description: welcomeValidationCount && welcomeValidationCount > 0
        ? `${welcomeValidationCount} active ${welcomeValidationCount === 1 ? 'validation is' : 'validations are'} already loaded and ready to issue.`
        : 'Issue parking validations and track how they are used.',
    },
    billing: { label: 'Billing', description: 'Review parking usage, invoices, and account activity.' },
    resident: { label: 'Resident guest parking', description: 'Manage resident records and guest allowances.' },
  }
  // Four keeps the welcome scannable on a laptop and a phone. Any additional
  // destination remains visible in the menu the footer points to.
  const welcomeSections = visibleTabs.slice(0, 4).map(tab => ({ tab, ...welcomeCopy[tab] }))
  const dismissWelcome = () => {
    dismissTenantWelcome(session, welcomeStorageFor(session))
    setWelcomeOpen(false)
  }

  const viewAsBanner = session.viewAs && (
    <>
      <ViewAsBanner
        name={session.viewAs.mode === 'full' ? me.tenant.name : (me.viewer?.name || session.viewAs.targetEmail || session.email)}
        level={me.access?.level} full={session.viewAs.mode === 'full'} onExit={exitViewAs}
        onHeightChange={setViewAsBannerHeight} />
      <ReadOnlyToast show={readOnlyToast} />
    </>
  )

  return (
    // Under a lens the shell keeps the banner's measured height free: the page
    // pads by it, and the sidebar, phone menu and dialogs read the same
    // variable (styles.css, TenantModal) so none of their controls sit under it.
    <div className="tenant-shell" style={{ minHeight: '100vh', display: 'flex', ...(session.viewAs ? { '--tenant-banner-h': `${viewAsBannerHeight}px`, paddingBottom: 'var(--tenant-banner-h)' } as CSSProperties : {}) }}>
      {viewAsBanner}
      {/* A sidebar (not the old top tab bar) because the tab count has grown
          4→8 over the portal's life and keeps growing — a flat `nowrap` row
          silently clips off-screen tabs below ~960px with no scroll or wrap
          affordance, while a vertical list scales for free by just growing
          the page. */}
      {/* STICKY, and pinned to the viewport height. The aside is a flex sibling of
          `main`, so it stretched to MAIN's height — 1800px+ on the Validation tab —
          which parked the footer far below the fold. That was survivable while the
          footer held only an email and Sign out; it is not once Settings lives
          there, because a primary destination would be unreachable without
          scrolling to the bottom of a 6000px page. Sticky also fixes the case that
          was already wrong: the nav itself scrolling away on a long screen.
          `overflowY: auto` so a short window can still reach Sign out. */}
      <TenantNavigation tenantName={me.tenant.name} context={`${session.token}:${me.tenant.id}`}>
        <img
          src="/parkify-logo-color.svg"
          alt="Parkify"
          className="tenant-brand-logo"
        />
        <TenantIdentity tenant={me.tenant} zones={me.zones} scopes={tenantScopes} onSwitch={switchTenant} />
        <nav className="tenant-nav" style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {/* Parkers leads — the people who park, matching the demo's own
              order. Permits sits right after: one is the requests waiting on
              the manager, the other is what those requests became. Settings
              (Tablets + Integrations) sits last — credential management a
              tenant visits far less often than the daily four above it. */}
          {/* Four items, not five. Settings moved to the FOOTER beside the
              signed-in email — it is account plumbing, not one of the four
              things a tenant opens this portal to do, and the demo has no
              Settings tab at all. */}
          {visibleTabs.map(t => (
            // Clearing the drill filter here is what keeps it a one-shot: an
            // ordinary nav click must never silently re-apply a filter set
            // minutes ago from a Permits capacity card.
            <button key={t} aria-current={effectiveTab === t ? 'page' : undefined} onClick={() => { setTab(t); setParkersDrill(null) }} style={tabStyle(effectiveTab === t)}>
              <NavIcon tab={t} />
              {t === 'parkers' ? 'Parkers'
                : t === 'permits' ? 'Permits'
                  : t === 'validation' ? 'Validation'
                    : t === 'billing' ? 'Billing' : 'Resident guest parking'}
            </button>
          ))}
        </nav>
        <div className="tenant-sidebar-spacer" style={{ flex: 1 }} />
        <div className="tenant-account" style={{ borderTop: '1px solid var(--color-gray-200)', paddingTop: 10, display: 'flex', flexDirection: 'column', gap: 8 }}>
          {/* Settings sits with the account, above the email it belongs to.
              Same nav treatment as the four above so it is obviously a
              destination and not a footer link. */}
          {canOpenSettings && (
            <button
              className="tenant-settings"
              aria-current={effectiveTab === 'settings' ? 'page' : undefined}
              onClick={() => { setTab('settings'); setParkersDrill(null) }}
              style={tabStyle(effectiveTab === 'settings')}
            >
              <NavIcon tab="settings" />
              Settings
            </button>
          )}
          <button
            className="tenant-access"
            onClick={() => setUsersOpen(true)}
            style={tabStyle(usersOpen)}
          >
            <NavIcon tab="users" />
            {tenantCan(me, 'users.manage') ? 'Manage access' : 'Your access'}
          </button>
          <span className="tenant-account-email" style={{ fontSize: 12, color: 'var(--color-gray-600)', wordBreak: 'break-word', padding: '0 12px' }}>{session.email}</span>
          <button className="tenant-signout" onClick={session.viewAs ? exitViewAs : signOut} style={{ fontSize: 12, fontWeight: 600, color: 'var(--color-gray-600)', background: 'transparent', border: '1px solid var(--color-gray-300)', borderRadius: 8, padding: '5px 12px', cursor: 'pointer' }}>{session.viewAs ? 'Exit view' : 'Sign out'}</button>
        </div>
      </TenantNavigation>

      {/* 1600, not 1100. The old cap left a 1512px laptop rendering 1100px of
          content inside a 1292px slot — 96px of dead gutter on each side, and a
          content column visually detached from the sidebar it belongs to, while
          the roster and sign-up-link rows squeezed. The demo frames at up to
          1600 with a 212px sidebar and lets content flex to fill, at true size
          with no transform scaling, so this is the width a prospect has been
          shown all along. `margin: '0 auto'` stays for genuinely wider
          monitors, where a measure does eventually need bounding. */}
      <main className="tenant-main">
        <div className="tenant-page-header">
          <div className="tenant-page-heading">
            <h1>{sectionTitle}</h1>
            <p>{sectionSubtitle}</p>
          </div>
          <div aria-label="Quick actions" style={{ display: 'flex', gap: 8, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
            {tenantCan(me, 'parkers.manage') && <AddParkerActions
              token={session.token}
              buttonStyle={quickActionSecondaryStyle}
              onAddManually={() => { setTab('parkers'); setOpenAddParker(true) }}
            />}
            {tenantCan(me, 'validations.issue') && tenantHasValidationProgram && <button onClick={() => { setHasValidationPrograms(true); setTab('validation'); setOpenIssueValidation(true) }} style={quickActionPrimaryStyle}>Issue validation</button>}
          </div>
        </div>
        {effectiveTab === 'validation'
          ? <Validation session={session} me={me} validationOnly={validationOnlyTenant} autoIssue={openIssueValidation} onAutoIssueHandled={() => setOpenIssueValidation(false)} />
          : effectiveTab === 'parkers'
            ? <Parkers session={session} me={me} hideMonetaryValues={validationOnlyTenant} initialTypeFilter={parkersDrill?.type ?? null} initialPermitFilter={parkersDrill?.permitId ?? null} autoOpenAdd={openAddParker} onAutoOpenAddHandled={() => setOpenAddParker(false)} />
            : effectiveTab === 'permits'
              ? <Permits
                  session={session}
                  hideMonetaryValues={validationOnlyTenant}
                  onSubtitleChange={setPermitSubtitle}
                  onViewParkers={(type, permitId) => { setParkersDrill({ type, ...(permitId ? { permitId } : {}) }); setTab('parkers') }}
                />
              : effectiveTab === 'billing'
                ? <Billing session={session} onSubtitleChange={setBillingSubtitle} />
                : effectiveTab === 'resident'
                  ? <ResidentImports session={session} showHeading={false} />
                  : <Settings session={session} onManageAccess={() => setUsersOpen(true)} />}
      </main>
      {usersOpen && (
        <UsersPanel
          session={session}
          me={me}
          onClose={() => setUsersOpen(false)}
          onSelfAccessChanged={async () => {
            setMe(await getMe(session.token))
            setUsersOpen(false)
          }}
        />
      )}
      {welcomeOpen && (
        <TenantWelcomeModal
          tenantName={me.tenant.name}
          sections={welcomeSections}
          onDismiss={dismissWelcome}
          onSelect={destination => {
            setTab(destination)
            setParkersDrill(null)
            dismissWelcome()
          }}
        />
      )}
    </div>
  )
}

/**
 * Public entry state for an unpaired validation tablet. Opening the
 * manager-generated `?d=ptd_…` link is the normal pairing action. A station that
 * cannot open links can paste that link (or its bare token) instead; the box
 * accepts ONLY that same opaque device token, checked by `isDeviceToken`, so it
 * never becomes a second credential format, and the server still verifies the
 * token on first use. After pairing, App.tsx renders the device-scoped Validate
 * screen.
 */
function ValidationPairingGate({ error, onPaired }: { error?: string; onPaired: (token: string, code?: string) => void }) {
  const [pairingValue, setPairingValue] = useState('')
  const [pairingError, setPairingError] = useState('')
  const pair = () => {
    const raw = pairingValue.trim()
    let token = raw
    let code = ''
    try {
      const url = new URL(raw)
      token = url.searchParams.get('d')?.trim() || ''
      code = url.searchParams.get('code')?.trim().toUpperCase() || ''
    } catch { /* a pasted token is also accepted */ }
    if (!isDeviceToken(token)) {
      setPairingError('That pairing code is not valid. Ask your manager for a fresh tablet link.')
      return
    }
    setPairingError('')
    onPaired(token, code || undefined)
  }
  return (
    <div style={{ minHeight: '100vh', background: '#faf9fb', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }}>
      <main style={{ width: 'min(520px, 100%)', background: '#fff', border: '1px solid #e7e2e9', borderRadius: 18, padding: '32px 28px', boxShadow: '0 14px 40px rgba(35,31,32,0.10)' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 28 }}>
          <img src="/parkify-logo-color.svg" alt="Parkify" style={{ height: 24, width: 'auto' }} />
          <span style={{ color: '#6b6470', fontSize: 13, fontWeight: 700 }}>Validation station</span>
        </div>
        <div style={{ width: 56, height: 56, borderRadius: 14, background: 'rgba(127,63,152,0.10)', color: '#7F3F98', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 28, marginBottom: 18 }} aria-hidden="true">⌁</div>
        <h1 style={{ margin: '0 0 8px', fontSize: 26, lineHeight: 1.15, letterSpacing: '-0.5px' }}>Station not paired</h1>
        <p style={{ margin: '0 0 18px', color: '#5f5863', fontSize: 15, lineHeight: 1.55 }}>
          Open the tablet link created by your Parkify manager to set this station up. The link pairs
          this device to one validation program and keeps the station signed in across shifts.
        </p>
        {(error || pairingError) && (
          <div role="alert" style={{ marginBottom: 18, padding: '11px 13px', borderRadius: 10, background: 'rgba(185,28,28,0.08)', border: '1px solid rgba(185,28,28,0.25)', color: '#991b1b', fontSize: 13, lineHeight: 1.45 }}>
            {pairingError || error}
          </div>
        )}
        <div style={{ padding: '14px 15px', borderRadius: 12, background: '#f6f3f7', color: '#514957', fontSize: 14, lineHeight: 1.5 }}>
          <b>Enter a pairing code to set this tablet up.</b>
          <div style={{ marginTop: 4, color: '#6b6470', fontSize: 13 }}>Ask the tenant manager to open Validation → Tablets and create a new tablet link.</div>
          <input
            value={pairingValue}
            onChange={e => { setPairingValue(e.target.value); setPairingError('') }}
            onKeyDown={e => { if (e.key === 'Enter') pair() }}
            placeholder="Paste the tablet link or pairing token"
            aria-label="Pairing code"
            autoComplete="off"
            style={{ width: '100%', boxSizing: 'border-box', marginTop: 12, padding: '11px 12px', borderRadius: 9, border: '1px solid #d9d1dd', fontSize: 13, fontFamily: 'monospace', background: '#fff' }}
          />
          <button
            onClick={pair}
            disabled={!pairingValue.trim()}
            style={{ width: '100%', marginTop: 10, padding: '12px 14px', border: 'none', borderRadius: 9, background: '#7F3F98', color: '#fff', fontFamily: 'inherit', fontSize: 14, fontWeight: 800, opacity: pairingValue.trim() ? 1 : 0.5, cursor: pairingValue.trim() ? 'pointer' : 'default' }}
          >
            Pair this station
          </button>
        </div>
        <a href="https://tenants.parkify.com" style={{ display: 'block', marginTop: 20, textAlign: 'center', color: '#7F3F98', fontSize: 14, fontWeight: 800, textDecoration: 'none' }}>
          Open Tenant Portal
        </a>
      </main>
    </div>
  )
}

const quickActionSecondaryStyle: CSSProperties = {
  ...ghostBtn, color: 'var(--color-gray-900)', padding: '8px 13px', fontSize: 12,
}
const quickActionPrimaryStyle: CSSProperties = {
  ...purpleBtn, padding: '8px 13px', fontSize: 12,
}

function tabStyle(active: boolean): CSSProperties {
  return {
    display: 'flex', alignItems: 'center', gap: 10, width: '100%', textAlign: 'left', padding: '9px 12px', borderRadius: 8, border: 'none',
    background: active ? 'var(--tenant-selection)' : 'transparent', color: active ? 'var(--tenant-selection-ink)' : 'var(--color-gray-600)',
    fontWeight: active ? 800 : 600, fontSize: 13, cursor: 'pointer', fontFamily: 'inherit',
  }
}

/**
 * Which lots this tenant is signed into, in ~180px of sidebar.
 *
 * A tenant with several zones used to see only `zones[0]` — a manager at a
 * two-deck property read "West Deck" and had no way to tell whether the portal
 * covered the other one. A plain join is not the fix either: zone display names
 * run long ("Stone Mountain Park — West Deck"), so three of them clip to an
 * ellipsis that hides the very fact this line exists to state.
 *
 * So: name what fits and COUNT the rest, and put the full list on `title`.
 * "+2 more" is a true statement at any zone count; a truncated join isn't.
 */
/**
 * Which lots this tenant is signed into, in ~140px of sidebar.
 *
 * A tenant with several zones used to see only `zones[0]` — a manager at a
 * two-deck property read "West Deck" and had no way to tell whether the portal
 * covered the other one.
 *
 * Returned as two PARTS rather than one string, because the two truncate
 * differently and only one of them may. Real lot names are long ("North
 * Overflow Lot"), so the name has to be allowed to clip — but if the whole
 * label is one ellipsised line, the "+1" clips off WITH it and the manager is
 * back to believing they have one lot. So the name shrinks and the count never
 * does: "North Overfl… +1" still answers the question.
 */
function zoneSummary(zones: MeResponse['zones']): { name: string; more: number } {
  if (zones.length === 0) return { name: '', more: 0 }
  return { name: zones[0].displayName, more: zones.length - 1 }
}

/**
 * The business identity at the top of the sidebar — logo (or a branded
 * initial badge, same fallback `StaffHeader` in Validate.tsx uses for the
 * kiosk masthead) plus which zone this is, so a manager signed into more
 * than one location can tell them apart at a glance instead of reading
 * "Tenant portal" under every tenant's name.
 */
function TenantIdentity({ tenant, zones, scopes, onSwitch }: { tenant: MeResponse['tenant']; zones: MeResponse['zones']; scopes: TenantScope[]; onSwitch: (tenantId: string) => void }) {
  const color = tenant.brandColor || '#7F3F98'
  const [logoBroken, setLogoBroken] = useState(false)
  const zoneLabel = zoneSummary(zones)
  return (
    <div className="tenant-identity">
      {tenant.logoUrl && !logoBroken ? (
        <img
          src={tenant.logoUrl} alt={`${tenant.name} logo`} onError={() => setLogoBroken(true)}
          style={{ width: 30, height: 30, borderRadius: '50%', objectFit: 'contain', background: '#fff', flexShrink: 0, boxShadow: '0 0 0 1px var(--color-gray-200)' }}
        />
      ) : (
        <div style={{ width: 30, height: 30, borderRadius: '50%', background: color, color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 800, fontSize: 13, flexShrink: 0 }}>
          {(tenant.name || '?').charAt(0).toUpperCase()}
        </div>
      )}
      <div style={{ minWidth: 0, flex: 1 }}>
        <div title={tenant.name} style={{ fontWeight: 800, fontSize: 13, lineHeight: 1.35, letterSpacing: '-0.2px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{tenant.name}</div>
        {/* `title` carries the FULL list even when the line is summarised or
            clipped — the summary is a space compromise, and a manager checking
            which lots they are signed into should never have to guess. */}
        {/* `title` carries the FULL list, since the name half is expected to
            clip on a real lot name. */}
        <div title={zones.map(z => z.displayName).join(', ') || undefined}
          style={{ fontSize: 11, fontWeight: 700, color: 'var(--color-gray-600)', display: 'flex', gap: 4, minWidth: 0, marginTop: 2 }}>
          <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {zoneLabel.name || 'Tenant portal'}
          </span>
          {/* flexShrink:0 is the whole point — see zoneSummary. */}
          {zoneLabel.more > 0 && (
            <span style={{ flexShrink: 0 }}>+{zoneLabel.more}</span>
          )}
        </div>
        {scopes.length > 1 && <select aria-label="Switch company" value={tenant.id} onChange={e => void onSwitch(e.target.value)} style={{ ...selectStyle, marginTop: 6, width: '100%', fontSize: 11, padding: '6px 8px' }}>
          {scopes.map(scope => <option key={scope.id} value={scope.id}>{scope.name}</option>)}
        </select>}
      </div>
    </div>
  )
}

/** 18px line icons, one per nav tab. Hand-rolled rather than a dependency —
 * nothing in this repo pulls an icon library, and eight glyphs isn't worth
 * becoming the first app that does. */
const ICON_PROPS = { width: 18, height: 18, viewBox: '0 0 24 24', fill: 'none', stroke: 'currentColor', strokeWidth: 1.8, strokeLinecap: 'round' as const, strokeLinejoin: 'round' as const }
const ICONS: Record<string, ReactNode> = {
  validation: <svg {...ICON_PROPS}><path d="M12 2l1.8 4.6 4.9.4-3.7 3.3 1.1 4.8L12 12.7l-4.1 2.4 1.1-4.8-3.7-3.3 4.9-.4z" /></svg>,
  parkers: <svg {...ICON_PROPS}><circle cx="9" cy="8" r="3" /><path d="M3.5 19c0-3 2.5-5 5.5-5s5.5 2 5.5 5" /><circle cx="17" cy="9" r="2.4" /><path d="M15.5 13.2c2.3.3 4 2 4 4.3" /></svg>,
  permits: <svg {...ICON_PROPS}><rect x="2.5" y="6" width="19" height="12" rx="2" /><path d="M2.5 10.5h19" strokeDasharray="1.5 2" /><circle cx="7" cy="14.2" r="1.1" /><path d="M10.5 14.2h7" /></svg>,
  billing: <svg {...ICON_PROPS}><rect x="2.5" y="5.5" width="19" height="13" rx="2" /><path d="M2.5 10h19" /><path d="M6 14.5h4" /></svg>,
  resident: <svg {...ICON_PROPS}><path d="M3 21V9l9-6 9 6v12" /><path d="M8 21v-6h8v6M3 21h18" /></svg>,
  users: <svg {...ICON_PROPS}><circle cx="9" cy="8" r="3" /><path d="M3.5 19c0-3 2.5-5 5.5-5s5.5 2 5.5 5" /><path d="M16 8v6M13 11h6" /></svg>,
  /* The canonical Lucide `settings` cog. The previous path was a mangled copy of
     it: relative arcs interleaved with ABSOLUTE `H`/`V` commands (`V20`, `H4`,
     `H10`, `V10`, `H20`) that yanked the pen to literal coordinates partway round
     the outline, so the teeth came out uneven with a visible break on the right
     edge. Invisible at 18px until you scale it up, which is how it shipped.
     If you replace this, render it at ~150px and LOOK before committing. */
  settings: <svg {...ICON_PROPS}><path d="M12.22 2h-.44a2 2 0 0 0-2 2v.18a2 2 0 0 1-1 1.73l-.43.25a2 2 0 0 1-2 0l-.15-.08a2 2 0 0 0-2.73.73l-.22.38a2 2 0 0 0 .73 2.73l.15.1a2 2 0 0 1 1 1.72v.51a2 2 0 0 1-1 1.74l-.15.09a2 2 0 0 0-.73 2.73l.22.38a2 2 0 0 0 2.73.73l.15-.08a2 2 0 0 1 2 0l.43.25a2 2 0 0 1 1 1.73V20a2 2 0 0 0 2 2h.44a2 2 0 0 0 2-2v-.18a2 2 0 0 1 1-1.73l.43-.25a2 2 0 0 1 2 0l.15.08a2 2 0 0 0 2.73-.73l.22-.39a2 2 0 0 0-.73-2.73l-.15-.08a2 2 0 0 1-1-1.74v-.5a2 2 0 0 1 1-1.74l.15-.09a2 2 0 0 0 .73-2.73l-.22-.38a2 2 0 0 0-2.73-.73l-.15.08a2 2 0 0 1-2 0l-.43-.25a2 2 0 0 1-1-1.73V4a2 2 0 0 0-2-2z" /><circle cx="12" cy="12" r="3" /></svg>,
}
function NavIcon({ tab }: { tab: string }) {
  return <span style={{ display: 'flex', flexShrink: 0 }}>{ICONS[tab]}</span>
}

function Centered({ children }: { children: React.ReactNode }) {
  return (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--color-gray-600)', fontSize: 14 }}>
      {children}
    </div>
  )
}

const GOOGLE_CONFIGURED = !!(import.meta.env.VITE_AUTH_GOOGLE_CLIENT_ID as string | undefined)

function GoogleLogo() {
  return (
    <svg width="18" height="18" viewBox="0 0 48 48" aria-hidden>
      <path fill="#FFC107" d="M43.6 20H42v-.1H24v8h11.3c-1.6 4.6-6 8-11.3 8-6.6 0-12-5.4-12-12s5.4-12 12-12c3 0 5.8 1.1 8 3l5.7-5.7C34.5 6.5 29.5 4.5 24 4.5c-10.8 0-19.5 8.7-19.5 19.5S13.2 43.5 24 43.5c10 0 19-7.5 19-19.5 0-1.4-.1-2.7-.4-4z"/>
      <path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.7 16 19 13 24 13c3 0 5.8 1.1 8 3l5.7-5.7C34.5 6.5 29.5 4.5 24 4.5c-7.6 0-14.2 4.3-17.7 10.2z"/>
      <path fill="#4CAF50" d="M24 43.5c5.4 0 10.3-2 14-5.4l-6.5-5.5C29.7 34 27 35 24 35c-5.3 0-9.7-3.4-11.3-8l-6.5 5C9.6 39.2 16.3 43.5 24 43.5z"/>
      <path fill="#1976D2" d="M43.6 20H42v-.1H24v8h11.3c-.8 2.3-2.2 4.3-4.1 5.7l6.5 5.5c-.5.4 6.8-5 6.8-15.1 0-1.4-.1-2.7-.4-4z"/>
    </svg>
  )
}

function SignIn({ error, onSignedIn }: {
  error: string
  onSignedIn: (session: { token: string; email: string; tenantId: string; viewAs?: import('./lib/session').ViewAsInfo }) => void
}) {
  const [email, setEmail] = useState('')
  const [sent, setSent] = useState(false)
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState('')
  const [staffBusy, setStaffBusy] = useState(false)
  const [staffToken, setStaffToken] = useState('')
  const [staffEmail, setStaffEmail] = useState('')
  const [staffTenants, setStaffTenants] = useState<StaffTenant[]>([])
  const [selectedTenant, setSelectedTenant] = useState('')

  const googleLogin = useGoogleLogin({
    flow: 'implicit',
    onSuccess: async (google) => {
      setStaffBusy(true); setErr('')
      try {
        const staff = await signInTenantStaffWithGoogle(google.access_token)
        const tenants = (await listStaffTenants(staff.token)).filter(tenant => tenant.active !== false)
        if (tenants.length === 0) throw new Error('No active tenants are available.')
        setStaffToken(staff.token); setStaffEmail(staff.email); setStaffTenants(tenants)
        setSelectedTenant(tenants[0]!.id)
      } catch (e) {
        setErr(e instanceof Error ? e.message : 'Parkify staff sign-in failed.')
      } finally { setStaffBusy(false) }
    },
    onError: () => setErr('Google sign-in was cancelled or failed.'),
    onNonOAuthError: () => setErr('Google sign-in failed (popup blocked or closed).'),
  })

  const enterTenant = async () => {
    if (!staffToken || !selectedTenant || staffBusy) return
    setStaffBusy(true); setErr('')
    try { onSignedIn(await openStaffTenant(staffToken, selectedTenant)) }
    catch (e) { setErr(e instanceof Error ? e.message : 'Could not open that tenant.') }
    finally { setStaffBusy(false) }
  }

  const submit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!email.trim() || busy) return
    setBusy(true); setErr('')
    try { await requestTenantLink(email.trim()); setSent(true) }
    catch { setErr('Could not send the link right now. Try again in a moment.') }
    finally { setBusy(false) }
  }

  return (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24, background: 'var(--color-gate-field, #F8F7F9)' }}>
      <div style={{ width: '100%', maxWidth: 420, background: '#fff', borderRadius: 16, padding: '36px 36px 30px', border: '1px solid var(--color-gray-300, #ECE9EF)', boxShadow: '0 12px 32px rgba(35,31,32,0.10), 0 2px 8px rgba(35,31,32,0.05)' }}>
        {/* Real logo asset here too — the sign-in card is white, so the same
            black-wordmark/purple-mark variant as the app header. */}
        <div style={{ display: 'flex', justifyContent: 'center' }}>
          <img src="/parkify-logo-color.svg" alt="Parkify" style={{ height: 24, width: 'auto', display: 'block' }} />
        </div>
        <div className="signin-product">TENANT PORTAL</div>
        {/* NOT "Validation portal". This app shipped as a validation-only
            surface and has since grown Parkers, Permits and Billing — a
            signed-in manager lands on Parkers, not Validation (#1801). The
            front door was the last place still advertising the old scope, which
            reads as "wrong app" to a manager sent here to approve staff. */}
        <h1 style={{ fontSize: 24, fontWeight: 800, textAlign: 'center', margin: '0 0 4px' }}>Sign in</h1>
        <p style={{ fontSize: 13, color: 'var(--color-gray-600)', textAlign: 'center', margin: '0 0 24px' }}>
          Approve who parks on your account, and see how your validations are being used.
        </p>
        {sent ? (
          <div style={{ background: 'rgba(98,197,132,0.12)', border: '1px solid rgba(98,197,132,0.4)', borderRadius: 10, padding: '14px 16px', fontSize: 13, color: '#15803d', lineHeight: 1.5 }}>
            Check your email — we sent a sign-in link to <b>{email}</b>. It's valid for 15 minutes.
          </div>
        ) : (
          <form onSubmit={submit}>
            <label style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.5px', textTransform: 'uppercase', color: 'var(--color-gray-600)' }}>Work email</label>
            <input
              type="email" value={email} onChange={e => setEmail(e.target.value)} autoFocus
              placeholder="you@yourbusiness.com"
              style={{ width: '100%', padding: '10px 12px', borderRadius: 8, border: '1.5px solid var(--color-gray-300)', fontSize: 14, margin: '6px 0 14px' }}
            />
            <button type="submit" disabled={busy || !email.trim()} style={{ width: '100%', padding: '12px', borderRadius: 10, background: '#7F3F98', color: '#fff', border: 'none', fontWeight: 700, fontSize: 14, cursor: 'pointer', opacity: busy || !email.trim() ? 0.6 : 1 }}>
              {busy ? 'Sending…' : 'Email me a sign-in link'}
            </button>
          </form>
        )}
        {(err || error) && <div style={{ marginTop: 14, fontSize: 12, color: '#b91c1c' }}>{err || error}</div>}
        {GOOGLE_CONFIGURED && (
          <div className="signin-staff">
            {staffToken ? (
              <>
                <label className="signin-label" htmlFor="staff-tenant">Open a tenant as {staffEmail}</label>
                <select id="staff-tenant" className="signin-select" value={selectedTenant} onChange={e => setSelectedTenant(e.target.value)}>
                  {staffTenants.map(tenant => <option key={tenant.id} value={tenant.id}>{tenant.name}</option>)}
                </select>
                <button className="signin-google" type="button" onClick={() => void enterTenant()} disabled={staffBusy || !selectedTenant}>
                  {staffBusy ? 'Opening…' : 'Open tenant portal'}
                </button>
              </>
            ) : (
              <button className="signin-google" type="button" onClick={() => googleLogin()} disabled={staffBusy}>
                <GoogleLogo /> {staffBusy ? 'Signing in…' : 'Parkify staff sign-in'}
              </button>
            )}
          </div>
        )}
        <p style={{ fontSize: 11, color: 'var(--color-gray-600)', textAlign: 'center', marginTop: 22, lineHeight: 1.5 }}>
          Access is by invitation from your parking operator. Don't have access? Ask them to add your email.
        </p>
      </div>
    </div>
  )
}
